import { useState, useEffect } from "react";
import { Button, Card } from "@mui/material";
import { Add, Visibility, Download } from "@mui/icons-material";
import { Repeat, Trash2, FileText } from "lucide-react";
import UploadDocumentModal from "../components/document/UploadDocumentModal";
import ConfirmDialog from "../components/common/ConfirmDialog";
import SuccessDialog from "../components/common/SuccessDialog";
import { getDocType, uploadDocumentAPI, getDocSubType, deleteDocumentAPI } from "../api/DocumentAPI";
import pdfIcon from "../../assets/pdf-icon.png"
import { Snackbar, Alert } from "@mui/material"

const DOC_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

const Documents = ({ docData: initialDocuments, onSaveSuccess }) => {
  const [documents, setDocuments] = useState(initialDocuments);
  const [openModal, setOpenModal] = useState(false);
  const [docType, setDocType] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const user_id = localStorage.getItem("userId")
  // For confirm/success
  const [confirmDialog, setConfirmDialog] = useState({ open: false, data: null });
  const [showSuccess, setShowSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // For replace
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceDocData, setReplaceDocData] = useState(null);
  const [lastAction, setLastAction] = useState("");

  const getDocTypes = async () => {
    try {
      const types = await getDocType();
      const data = types.data || [];
      setDocType(data);
      if (data.length > 0) {
        setSelectedType(data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch document types:", error);
    }
  };

  useEffect(() => {
    getDocTypes();
  }, []);
  useEffect(() => {
    setDocuments(initialDocuments || [])
  }, [initialDocuments])
  /** -------------------
   * When Upload clicked
   * ------------------- */
  const handleUpload = (payload) => {
    setConfirmDialog({ open: true, data: payload });
  };

  /** -------------------
   * Confirm Upload -> API
   * ------------------- */
  const handleConfirm = async () => {
    const payload = confirmDialog.data;
    if (!payload) return;

    
    try {
      const response = await uploadDocumentAPI(payload);

      if (response?.success) {
        setConfirmDialog({ open: false, data: null });
        setOpenModal(false);
        setReplaceMode(false);
        setReplaceDocData(null);
        setShowSuccess(true);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Failed to upload document.",
        });
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setSnackbar({ open: true, message: "Something went wrong. Try again." });
    }
  };

  const handleDelete = (doc) => {
    setConfirmDialog({
      open: true,
      type: "delete",
      data: doc,
    });
  };

  const handleConfirmDelete = async () => {
    const doc = confirmDialog.data;
    if (!doc) return;
    let payload = { user_id: user_id, id: doc.id }
    try {
      const response = await deleteDocumentAPI(payload); // Make sure you have this API
      if (response?.success) {
        setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
        setConfirmDialog({ open: false, data: null });
        setLastAction("delete");
        setShowSuccess(true);
      } else {
        setSnackbar({ open: true, message: response?.message || "Failed to delete document." });
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setSnackbar({ open: true, message: "Something went wrong. Try again." });
    }
  };

  const handleDownload = async (doc) => {
    try {
      const fileUrl = new URL(doc.document_file, DOC_ENDPOINT).toString();
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = doc.document_type_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  /** -------------------
   * Handle Replace Click
   * ------------------- */
  const handleReplace = async (doc) => {
    setReplaceMode(true);
    setReplaceDocData(doc);

    // Pre-fill doc type and subtype
    const typeId = doc.document_stage;
    const subtypeId = doc.document_type;
    setOpenModal(true);

    setReplaceDocData({ ...doc, typeId, subtypeId });
    setSelectedType(doc.document_stage);
  };

  return (
    <div className="w-full">
      {/* Tabs */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          {docType.map((tab) => (
            <button
              key={tab.document_stage}
              onClick={() => setSelectedType(tab.id)}
              className={`rounded-full px-4 py-2 font-altisunisa font-medium text-base ${selectedType === tab.id
                ? "bg-[#FFE8FB] text-[#9D0385] font-semibold hover:bg-[#FFE8FB]"
                : "text-gray-700 border border-gray-300 hover:bg-gray-100"
                }`}
            >
              {tab.document_stage}
            </button>
          ))}
        </div>

        {/* Add New */}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setReplaceMode(false);
            setReplaceDocData(null);
            setOpenModal(true);
          }}
          className="bg-[#9D0385] text-white px-4 hover:bg-[#9D0385] font-altisunisa font-semibold"
        >
          Add New Documents
        </Button>
      </div>

      {/* Document List */}
      <div className="space-y-3 mt-4">
        <Card>
          <div className="rounded-xl border border-gray-300 font-altisunisa font-medium text-gray-700">
            {(() => {
              const filteredDocs = documents.filter(
                (doc) => doc.document_stage === selectedType
              );

              if (filteredDocs.length === 0) {
                const currentStage =
                  docType.find((d) => d.id === selectedType)?.document_stage || "";
                return (

                  <div className="flex gap-2 flex-col justify-center items-center py-4">
                    <span className="p-4 rounded-full bg-[#FFE8FB]">
                      <FileText className="text-[#9D0385] " />
                    </span>
                    <div className="text-center text-[#9D0385] text-2xl font-siromi">
                      No Documents Added for{" "}
                      {currentStage}{" "}
                    </div>

                    <span className="text-center text-black w-[50%] text-lg mx-auto px-6 font-altisunisa font-medium">
                      You haven't uploaded any documents yet. Add your {currentStage} documents to complete your profile.
                    </span>
                  </div>
                );
              }

              return filteredDocs.map((doc, index) => (
                <div
                  key={doc.id}
                  className={`flex justify-between rounded-xl items-center p-3 font-altisunisa ${index % 2 === 0 ? "bg-[#FBFBFB]" : "bg-white"
                    }`}
                >
                  {/* Left */}
                  <div className="flex items-center gap-3">
                    <img src={pdfIcon} className="w-12" />
                    <div>
                      <div className="font-semibold font-altisunisa">
                        {doc.document_type_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Last update :{" "}
                        {new Date(doc.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        window.open(
                          new URL(doc.document_file, DOC_ENDPOINT).toString(),
                          "_blank"
                        )
                      }
                      className="px-4 py-1 rounded-md border text-[#572575] font-semibold border-[#572575] hover:bg-gray-100 text-sm flex items-center gap-1"
                    >
                      <Visibility className="text-[#572575] w-4 h-4" /> View
                    </button>
                    <button
                      onClick={() => handleReplace(doc)}
                      className="px-4 py-1 rounded-md border text-[#572575] font-semibold border-[#572575] hover:bg-gray-100 text-sm flex items-center gap-1"
                    >
                      <Repeat className="text-[#572575] w-4 h-4" /> Replace
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="px-4 py-1 rounded-md border text-[#572575] font-semibold border-[#572575] hover:bg-gray-100 text-sm flex items-center gap-1"
                    >
                      <Download className="text-[#572575] w-4 h-4" /> Download
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      className="px-4 py-1 rounded-md border text-red-500 font-semibold border-red-500 hover:bg-gray-100 text-sm flex items-center gap-1"
                    >
                      <Trash2 className="text-red-500 w-4 h-4" /> Delete
                    </button>
                  </div>
                </div>
              ));
            })()}
          </div>
        </Card>
      </div>


      {/* Upload Modal */}
      <UploadDocumentModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        selectedDocStage={selectedType}
        onUpload={handleUpload}
        replaceMode={replaceMode}
        replaceDocData={replaceDocData}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === "delete"
            ? "Confirm Delete"
            : replaceMode
              ? "Confirm Replace"
              : "Confirm Upload"
        }
        message={
          confirmDialog.type === "delete"
            ? "Are you sure you want to delete this document?"
            : replaceMode
              ? "Are you sure you want to replace this document with a new one?"
              : "Are you sure you want to upload this document?"
        }
        onCancel={() => setConfirmDialog({ open: false, data: null })}
        onConfirm={
          confirmDialog.type === "delete" ? handleConfirmDelete : handleConfirm
        }
      />


      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccess}
        message={
          lastAction === "delete"
            ? "Your document has been deleted successfully."
            : replaceMode
              ? "Your document has been replaced successfully."
              : "Your document has been uploaded successfully."
        }
        onClose={() => setShowSuccess(false)}
        onSuccess={() => {
          setShowSuccess(false);
          if (onSaveSuccess) onSaveSuccess();
        }}
      />

      {/* Snackbar for messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>


    </div>
  );
};

export default Documents;
